import React, { useState } from 'react';
import {
  GraduationCap,
  Award,
  Star,
  Target,
  Compass,
  Headphones,
  BookOpen,
  UserCheck,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Plus,
  Minus,
  CheckCircle2
} from 'lucide-react';
import './AboutPage.css';

function AboutPage({ onNavigateToContact }) {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const principlesList = [
    {
      id: 1,
      icon: <Headphones size={24} className="principle-icon" />,
      title: "Listen First",
      desc: "Good guidance begins with understanding the person, not only the academic marks."
    },
    {
      id: 2,
      icon: <BookOpen size={24} className="principle-icon" />,
      title: "Explain Clearly",
      desc: "Students should understand their options and the reasoning behind every decision."
    },
    {
      id: 3,
      icon: <UserCheck size={24} className="principle-icon" />,
      title: "Treat Personally",
      desc: "Every plan should reflect individual goals, needs, interests, and circumstances."
    },
    {
      id: 4,
      icon: <Award size={24} className="principle-icon" />,
      title: "Build Confidence",
      desc: "Guidance should help people feel more capable and self-aware, not dependent."
    },
    {
      id: 5,
      icon: <TrendingUp size={24} className="principle-icon" />,
      title: "Progress Thoughtfully",
      desc: "Meaningful progress comes from guided challenge, consistency, and support."
    },
    {
      id: 6,
      icon: <Sparkles size={24} className="principle-icon" />,
      title: "Work Actively",
      desc: "Hands-on assessment, practical roadmaps, and goal execution work together."
    }
  ];

  const faqsList = [
    {
      id: 1,
      question: "What age groups and student levels does Deepclariti support?",
      answer: "Deepclariti provides tailored career guidance for Class 7–8 ('Beyond Marks'), Class 9–10 ('Future Pathways'), Class 11–12 ('Career Discovery'), undergraduate & postgraduate college students ('Career Launch' & 'Acceleration'), as well as young working professionals."
    },
    {
      id: 2,
      question: "How do Deepclariti's personalised career coaching sessions work?",
      answer: "Our sessions begin with scientifically validated psychometric assessments, followed by one-on-one guided self-discovery sessions with internationally certified career coach V. Swaminathan to align strengths, interests, and future academic/career pathways."
    },
    {
      id: 3,
      question: "Do you involve parents in the guidance process?",
      answer: "Yes! We place a strong emphasis on parent-student alignment. We conduct dedicated family discussions to foster shared understanding, reduce academic stress, and build confidence around stream and career choices."
    },
    {
      id: 4,
      question: "How can I schedule a consultation session with Deepclariti?",
      answer: "You can easily book a free 45-minute consultation by filling out the contact form or clicking 'Schedule a Consultation' below."
    }
  ];

  return (
    <div className="about-page">

      {/* ================= SECTION 1: HERO HEADER & AUTOMATIC SMOOTH SLIDING GALLERY ================= */}
      <div className="about-hero-section">
        <div className="container">
          <div className="about-hero-header text-center">
            <span className="about-badge-pill">ABOUT</span>
            <h1 className="about-hero-title">
              Boutique by choice, <span className="title-highlight-italic">not by circumstance</span>
            </h1>
            <p className="about-hero-subtitle">
              Deepclariti is a firm built around a single premise: the matters that most affect a student’s future — or a person’s career — should reach senior judgment, not get delegated past it. We’ve kept our guidance personalized because individual focus is what makes the work possible.
            </p>
          </div>
        </div>

        {/* Automatic Infinite Sliding Image Track */}
        <div className="auto-marquee-outer">
          <div className="auto-marquee-track">
            {/* Set 1 of 6 images */}
            <div className="gallery-card-item">
              <img src="/images/Home/About1.jpeg" alt="Student guidance group" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/Home/About2.jpeg" alt="1-on-1 coaching session" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/about-executive.jpg" alt="Executive mentor" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/instructors-team.jpg" alt="Career advisory team" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/feature-writing.jpg" alt="Goal planning & analysis" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/feature-coffee.jpg" alt="Pathway exploration" />
            </div>

            {/* Set 2 (Duplicates for 100% Seamless Infinite Loop) */}
            <div className="gallery-card-item">
              <img src="/images/Home/About1.jpeg" alt="Student guidance group duplicate" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/Home/About2.jpeg" alt="1-on-1 coaching session duplicate" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/about-executive.jpg" alt="Executive mentor duplicate" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/instructors-team.jpg" alt="Career advisory team duplicate" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/feature-writing.jpg" alt="Goal planning & analysis duplicate" />
            </div>
            <div className="gallery-card-item">
              <img src="/images/feature-coffee.jpg" alt="Pathway exploration duplicate" />
            </div>
          </div>
        </div>
      </div>

      <div className="container padding-y">

        {/* ================= SECTION 2: OUR PHILOSOPHY & OUR MISSION ================= */}
        <div className="about-philosophy-grid">

          {/* Left Column: Philosophy */}
          <div className="philosophy-col">
            <span className="section-meta-label">/ OUR PHILOSOPHY /</span>
            <h2 className="philosophy-title">Why Deepclariti Began</h2>

            <div className="philosophy-img-box">
              <img src="/images/feature-writing.jpg" alt="Why Deepclariti Began" />
            </div>

            <p className="philosophy-desc">
              Academic marks, stress, pressure, and career choices can all influence how someone develops and succeeds. Our approach connects individual care with practical roadmaps, clear education, and realistic planning.
            </p>
          </div>

          {/* Right Column: Mission */}
          <div className="philosophy-col">
            <span className="section-meta-label">/ OUR MISSION /</span>
            <h2 className="philosophy-title">Help People Move Forward</h2>

            <div className="philosophy-img-box">
              <img src="/images/Home/About2.jpeg" alt="Help People Move Forward" />
            </div>

            <div className="mission-bullets-grid">
              <div className="bullet-item">
                <CheckCircle2 size={16} className="bullet-icon" />
                <span>Understand their condition</span>
              </div>
              <div className="bullet-item">
                <CheckCircle2 size={16} className="bullet-icon" />
                <span>Make sustainable progress</span>
              </div>
              <div className="bullet-item">
                <CheckCircle2 size={16} className="bullet-icon" />
                <span>Feel involved in guidance</span>
              </div>
              <div className="bullet-item">
                <CheckCircle2 size={16} className="bullet-icon" />
                <span>Develop practical habits</span>
              </div>
              <div className="bullet-item">
                <CheckCircle2 size={16} className="bullet-icon" />
                <span>Build confidence in movement</span>
              </div>
              <div className="bullet-item">
                <CheckCircle2 size={16} className="bullet-icon" />
                <span>Return to meaningful activity</span>
              </div>
            </div>
          </div>

        </div>

        {/* ================= SECTION 3: KEY IMPACT METRICS BAR ================= */}
        <div className="about-stats-bar">
          <div className="stats-bar-item">
            <div className="stat-icon-pill">
              <GraduationCap size={28} />
            </div>
            <div className="stat-text-box">
              <h3 className="stat-big-number">15k+</h3>
              <p className="stat-label-desc">Learners transformed through our platform.</p>
            </div>
          </div>

          <div className="stats-bar-item">
            <div className="stat-icon-pill">
              <Award size={28} />
            </div>
            <div className="stat-text-box">
              <h3 className="stat-big-number">10+</h3>
              <p className="stat-label-desc">Years of experience in delivering quality education.</p>
            </div>
          </div>

          <div className="stats-bar-item">
            <div className="stat-icon-pill">
              <Star size={28} />
            </div>
            <div className="stat-text-box">
              <h3 className="stat-big-number">95%</h3>
              <p className="stat-label-desc">Satisfaction rate from our global community.</p>
            </div>
          </div>
        </div>

        {/* ================= SECTION 4: EMPOWERING LEARNERS FOR A BRIGHTER FUTURE ================= */}
        <div className="empower-section-grid">
          <div className="empower-img-box">
            <img src="/images/about-headphones.jpg" alt="Empowering Learners" />
          </div>

          <div className="empower-content-box">
            <span className="empower-pill-badge">Our Story</span>

            <h2 className="empower-title">
              Empowering Learners<br />
              for a <span className="title-highlight-italic">Brighter Future</span>
            </h2>

            <p className="empower-desc">
              At Deepclariti, we empower individuals by unlocking their potential through quality education and personalized guidance. We equip learners with tools, resources, and expertise to excel.
            </p>

            <hr className="empower-divider" />

            {/* Block 1: Our Mission */}
            <div className="empower-block-item">
              <div className="block-icon-circle">
                <Target size={22} />
              </div>
              <div className="block-text-area">
                <h4 className="block-item-title">Our Mission</h4>
                <p className="block-item-desc">
                  To make education accessible and impactful, empowering individuals to achieve their dreams and create a better future through knowledge.
                </p>
              </div>
            </div>

            <hr className="empower-divider" />

            {/* Block 2: Our Vision */}
            <div className="empower-block-item">
              <div className="block-icon-circle">
                <Compass size={22} />
              </div>
              <div className="block-text-area">
                <h4 className="block-item-title">Our Vision</h4>
                <p className="block-item-desc">
                  To be a global leader in education, fostering innovation, collaboration, and excellence to transform lives and communities.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ================= SECTION 5: PRINCIPLES BEHIND EVERY PLAN ================= */}
        <div className="about-principles-section">
          <div className="text-center margin-bottom-lg">
            <span className="section-meta-label">/ WHAT GUIDES US /</span>
            <h2 className="principles-main-title">Principles Behind Every Plan</h2>
          </div>

          <div className="principles-cards-grid">
            {principlesList.map((item) => (
              <div key={item.id} className="principle-card">
                <div className="principle-icon-wrapper">
                  {item.icon}
                </div>
                <h3 className="principle-card-title">{item.title}</h3>
                <p className="principle-card-desc">{item.desc}</p>
                
                <button onClick={onNavigateToContact} className="btn-principle-link">
                  <span>Book a Consultation</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ================= SECTION 6: FAQS SECTION ================= */}
        <div className="contact-faqs-section">
          <div className="contact-faqs-grid">

            <div className="faqs-left-col">
              <h2 className="faqs-title">
                Deepclariti Career<br />
                Guidance – FAQs
              </h2>
              <p className="faqs-subtitle">
                Answers to common questions about our personalised career coaching, assessments, and student guidance programs.
              </p>
            </div>

            <div className="faqs-right-col">
              <div className="contact-accordion-list">
                {faqsList.map((faq, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div key={faq.id} className={`contact-faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-header-btn"
                        onClick={() => toggleFaq(idx)}
                      >
                        <span className="faq-question-text">{faq.question}</span>
                        <span className="faq-toggle-icon">
                          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="faq-body-content">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>

        {/* ================= SECTION 7: KICKSTART BANNER ================= */}
        <div className="founder-kickstart-banner">
          <div className="kickstart-badge-pill">
            READY TO TAKE THE NEXT STEP?
          </div>

          <h2 className="kickstart-banner-title">
            The Right Guidance Can Change<br />
            a Student’s Entire Future.
          </h2>

          <p className="kickstart-banner-desc">
            Whether you are a student searching for direction or a parent seeking clarity, Deepclariti™ helps you make informed and confident decisions about the road ahead.
          </p>

          <button
            onClick={onNavigateToContact}
            className="btn-kickstart-action"
          >
            Schedule a Consultation
          </button>
        </div>

      </div>
    </div>
  );
}

export default AboutPage;
